import React, { useState } from 'react'
import { Range, Transforms, Editor } from 'slate'
import { ReactEditor, useSlateStatic } from 'slate-react'
import { cbModal, ModalBody, ModalHeader, Button, TextInput, Icon, Field, EditableTags, ModalFooter, Pills } from '../../../../../../index'
import { Registry } from '@react-registry'

import { setClass, setId, getClass, getId } from './utils'
import { setOldSelection } from '../../../utils/setOldSelection'
import { elementCategories } from '../elementCategories'
import { PropertyIcon } from '../../utils/Icon'
import { JsonNode } from '../../../utils/types'
import { v4 } from 'uuid'
import { IPill } from '../../../../../Pills/type'

function openPropertyModal(event, editor) {
  event?.preventDefault()
  if (editor.selection) {
    const initialClassName = getClass(editor, editor.selection)
    const initialId = getId(editor, editor.selection)

    cbModal({
      modalProps: {
        shouldReturnFocusAfterClose: false,
        onClose: () => setOldSelection(editor)
      },
      component: (props) => (
        <PropertyOverlayModal
          {...props}
          editor={editor}
          selection={editor.selection}
          initialClassName={initialClassName}
          initialId={initialId}
        />
      )
    })
  }
}

export function PropertyOverlayWrapper(props) {
  const editor = useSlateStatic()
  const handleMouseDown = (event) => {
    openPropertyModal(event, editor)
  }

  return <React.Fragment>{props.children({ handleMouseDown })}</React.Fragment>
}
export function splitItemsOnSpace(items: IPill[]) {
  const newItems = items.flatMap((item) => {
    if (item.text.search(" ") === -1) return [item]
    return item.text.split(" ").map((text) => ({ text, id: v4().split('-').join('') }))
  })
  return newItems
}
export function PropertyOverlayModal(props) {
  const { selection, initialClassName, initialId, editor } = props

  const [classVal, setClassVal] = useState(initialClassName)
  const [idVal, setIdVal] = useState(initialId)

  const [applied, setApplied] = useState(false)

  function isIdPropertyDisabled() {
    if (Range.isExpanded(editor.selection)) {
      const [table] = Editor.nodes<JsonNode>(editor, { at: selection, match: (n: JsonNode) => n.type === 'table' })

      if (table) {
        const [, tablePath] = table
        const tableRange = Editor.range(editor, tablePath)

        const isSelectionOnlyInTable =
          Range.includes(tableRange, Range.start(editor.selection)) &&
          Range.includes(tableRange, Range.end(editor.selection))

        if (isSelectionOnlyInTable) {
          return true
        }
      }
    }

    return false
  }


  const handleClassPlace = () => {
    Transforms.select(editor, selection)
    setClass(editor, classVal, selection)
  }
  const handleClassClear = () => {
    Transforms.select(editor, selection)
    setClassVal('')
    setClassValPill([])
    setClass(editor, '', selection)
  }

  // Id functions
  const handleIdPlace = () => {
    Transforms.select(editor, selection)
    setId(editor, idVal, selection)
  }
  const handleIdClear = () => {
    Transforms.select(editor, selection)
    setIdVal('')
    setIdValPill([])
    setId(editor, '', selection)
  }

  const handleClear = () => {
    handleClassClear()
    handleIdClear()
    setApplied(false)
  }

  const handleApply = () => {
    handleClassPlace()
    handleIdPlace()
    setApplied(true)
    closeModal()
  }

  const closeModal = () => {
    props.closeModal()
    ReactEditor.focus(editor)
    Transforms.select(editor, editor.savedSelection)
  }

  const [classValPill, setClassValPill] = useState(() => {
    return classVal && classVal.length > 0 ? classVal.split(' ').map(text => ({ text, id: v4().split('-').join('') })) : []
  })

  const [idValPill, setIdValPill] = useState(() => {
    return idVal && idVal.length > 0 ? idVal.split(' ').map(text => ({ text, id: v4().split('-').join('') })) : []
  })

  return (
    <div className='cs-auto-draft-modal'>
      <ModalHeader title="Add Property" closeModal={closeModal} />

      <ModalBody className="modalBodyCustomClass">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Field labelText="Class">
            <Pills
              isEditable={true}
              isSortable={true}
              variant="chip"
              placeholder="Enter Class Property"
              onChange={(changedItems) => {
                const newItems = splitItemsOnSpace(changedItems)
                setClassValPill(newItems)
                setApplied(false)
                setClassVal(newItems.map((item) => item.text).join(' '))
              }}
              testId="property_class_input"
              canTruncate={true}
              isDisabled={false}
              shouldHaveBorder={true}
              items={classValPill}
            />
          </Field>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Field labelText="ID">
            <Pills
              isEditable={true}
              isSortable={true}
              variant="chip"
              placeholder="Enter Id Property"
              onChange={(changedItems) => {
                const newItems = splitItemsOnSpace(changedItems)
                setIdValPill(newItems)
                setApplied(false)
                setIdVal(newItems.map((item) => item.text).join(' '))
              }}
              testId="property_id_input"
              canTruncate={true}
              isDisabled={false}
              shouldHaveBorder={true}
              items={idValPill}
            />
          </Field>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button id="applyBtn" version="v2" onClick={handleApply}>{applied ? 'Applied' : 'Apply'}</Button>
        <span style={{ marginLeft: '16px' }} />
        <Button id="clearBtn" onClick={handleClear} version="v2" buttonType="secondary" disabled={!idVal && !classVal}>
          Clear
        </Button>
        <Button version='v2' onClick={closeModal} buttonType='tertiary'>Cancel</Button>
      </ModalFooter>
    </div>
  )
}

export function shortcutOpenPropertyModal(event, editor, format, registryObj) {
  openPropertyModal(event, editor)
}

Registry.register(
  {
    iconName: <PropertyIcon />,
    title: 'Add Properties',
    shortcut: { key: 'mod+shift+p', callback: shortcutOpenPropertyModal },
    IngressComponent: PropertyOverlayWrapper,
    category: elementCategories.PROPERTY,
    isContentStackElement: true,
    inBasicToolbar: false,
    canCustomize: false
  },
  { id: 'property', registry: 'element' }
)
